/**
 * Drizzle ORM type mappings for schema fields and form fields
 */

import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import {
  assertSupportedFormFieldTree,
  assertSupportedFormFieldType,
  assertSupportedSchemaFieldTree,
  assertSupportedSchemaFieldType
} from '@core-engine/schema/type-mappers/validation.js'
import type { FormField, SchemaField } from '@core-engine/schema/types.js'
import { quotePropertyName } from '@core-engine/utils/string.js'

// ============================================================================
// Constants
// ============================================================================

export const DRIZZLE_DEFAULTS = {
  VARCHAR_LENGTH: 255,
  DECIMAL_PRECISION: 10,
  DECIMAL_SCALE: 2
} as const

// ============================================================================
// Schema Field → Drizzle Type (for database generator)
// ============================================================================

/**
 * Map a schema field type to Drizzle ORM column type
 * @param field - The schema field
 * @param requiredImports - Set to track which Drizzle imports are needed
 * @returns The Drizzle column type expression
 */
export function toDrizzleType(field: SchemaField, requiredImports: Set<string>): string {
  assertSupportedSchemaFieldTree(field)

  switch (field.type) {
    case 'uuid':
    case 'serial':
      requiredImports.add('uuid')
      return 'uuid().defaultRandom()'
    case 'string':
    case 'email':
    case 'varchar':
      requiredImports.add('varchar')
      return field.length
        ? `varchar({ length: ${field.length} })`
        : `varchar({ length: ${DRIZZLE_DEFAULTS.VARCHAR_LENGTH} })`
    case 'image':
    case 'video':
    case 'media':
      requiredImports.add('text')
      return 'text()'
    case 'text':
    case 'markdown':
    case 'richtext':
      requiredImports.add('text')
      return 'text()'
    case 'number':
      requiredImports.add('integer')
      return 'integer()'
    case 'decimal':
      requiredImports.add('decimal')
      if (field.precision !== undefined && field.scale !== undefined) {
        return `decimal({ precision: ${field.precision}, scale: ${field.scale}, mode: 'number' })`
      }
      return `decimal({ precision: ${DRIZZLE_DEFAULTS.DECIMAL_PRECISION}, scale: ${DRIZZLE_DEFAULTS.DECIMAL_SCALE}, mode: 'number' })`
    case 'boolean':
      requiredImports.add('boolean')
      return 'boolean()'
    case 'timestamp':
      requiredImports.add('timestamp')
      return "timestamp({ precision: 3, mode: 'string' })"
    case 'date':
      requiredImports.add('date')
      return "date({ mode: 'string' })"
    case 'time':
    case 'icon':
      requiredImports.add('varchar')
      return field.length
        ? `varchar({ length: ${field.length} })`
        : `varchar({ length: ${DRIZZLE_DEFAULTS.VARCHAR_LENGTH} })`
    case 'select':
      if (field.multiple) {
        requiredImports.add('jsonb')
        return 'jsonb().$type<string[]>()'
      }
      requiredImports.add('varchar')
      return field.length
        ? `varchar({ length: ${field.length} })`
        : `varchar({ length: ${DRIZZLE_DEFAULTS.VARCHAR_LENGTH} })`
    case 'gallery':
      requiredImports.add('jsonb')
      return 'jsonb().$type<string[]>()'
    case 'list':
      requiredImports.add('jsonb')
      if (field.fields && field.fields.length > 0) {
        const nestedType = buildNestedListType(field.fields)
        return `jsonb().$type<Array<{ ${nestedType} }>>()`
      }
      return 'jsonb().$type<string[]>()'
    case 'relationship':
      if (isUserRelationshipTarget(field.relationship)) {
        requiredImports.add('text')
        return 'text()'
      }
      requiredImports.add('uuid')
      return 'uuid()'
    default:
      requiredImports.add('text')
      return 'text()'
  }
}

/**
 * Build nested type string for list fields with nested fields
 */
function buildNestedListType(fields: SchemaField[]): string {
  return fields
    .flatMap((f) => {
      assertSupportedSchemaFieldType(f)

      let type: string
      if (f.type === 'list' && f.fields && f.fields.length > 0) {
        const innerType = buildNestedListType(f.fields)
        type = `Array<{ ${innerType} }>`
      } else if ((f.type === 'group' || f.type === 'section') && f.fields && f.fields.length > 0) {
        type = `{ ${buildNestedListType(f.fields)} }`
      } else if (f.type === 'list') {
        type = 'string[]'
      } else if (f.type === 'number' || f.type === 'decimal') {
        type = 'number'
      } else if (f.type === 'boolean') {
        type = 'boolean'
      } else {
        type = 'string'
      }
      const result = [`${quotePropertyName(f.name)}?: ${type}`]
      if (f.hasIcon) {
        result.push(`${quotePropertyName(`${f.name}Icon`)}?: string`)
      }
      return result
    })
    .join('; ')
}

// ============================================================================
// Form Field → Drizzle Type (for form-pipeline)
// ============================================================================

/**
 * Map a form field type to Drizzle ORM column type
 * @param field - The form field
 * @param requiredImports - Set to track which Drizzle imports are needed
 * @returns The Drizzle column type expression
 */
export function formFieldToDrizzleType(field: FormField, requiredImports: Set<string>): string {
  assertSupportedFormFieldTree(field)

  switch (field.type) {
    case 'text':
      requiredImports.add('varchar')
      return field.maxLength
        ? `varchar({ length: ${field.maxLength} })`
        : `varchar({ length: ${DRIZZLE_DEFAULTS.VARCHAR_LENGTH} })`
    case 'textarea':
      requiredImports.add('text')
      return 'text()'
    case 'email':
      requiredImports.add('varchar')
      return `varchar({ length: ${DRIZZLE_DEFAULTS.VARCHAR_LENGTH} })`
    case 'phone':
      requiredImports.add('varchar')
      return 'varchar({ length: 50 })'
    case 'number':
      requiredImports.add('integer')
      return 'integer()'
    case 'url':
      requiredImports.add('varchar')
      return 'varchar({ length: 500 })'
    case 'date':
      requiredImports.add('date')
      return "date({ mode: 'string' })"
    case 'select':
    case 'radio':
    case 'timezone':
      requiredImports.add('varchar')
      return `varchar({ length: ${DRIZZLE_DEFAULTS.VARCHAR_LENGTH} })`
    case 'checkbox':
      requiredImports.add('boolean')
      return 'boolean()'
    case 'multiselect':
      requiredImports.add('jsonb')
      return 'jsonb().$type<string[]>()'
    case 'list':
      requiredImports.add('jsonb')
      if (field.fields && field.fields.length > 0) {
        for (const nestedField of field.fields) {
          assertSupportedFormFieldType(nestedField)
        }
        return 'jsonb().$type<Record<string, unknown>[]>()'
      }
      return 'jsonb().$type<string[]>()'
    case 'file':
    case 'upload':
      requiredImports.add('text')
      return 'text()'
    default:
      requiredImports.add('text')
      return 'text()'
  }
}
